import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Tag,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Info
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterAudience, setFilterAudience] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "general",
    target_audience: "all",
    priority_order: 0,
    is_active: true
  });

  const navigate = useNavigate();
  const adminId = localStorage.getItem("adminId") || localStorage.getItem("userid") || localStorage.getItem("userId");

  // Fetch FAQs
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const filters = new URLSearchParams();
      if (filterCategory !== "all") filters.append("category", filterCategory);
      if (filterAudience !== "all") filters.append("target_audience", filterAudience);
      if (filterStatus !== "all") filters.append("is_active", filterStatus === "active");
      
      const response = await fetch(`${API_URL}/faqs?${filters.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setFaqs(data.data);
      } else {
        setError("Failed to fetch FAQs");
      }
    } catch (error) {
      setError("Failed to fetch FAQs. Please try again later.");
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/faqs/categories/all`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${API_URL}/faqs/statistics`);
      const data = await response.json();
      
      if (data.success) {
        setStatistics(data.data);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Fetch analytics for a specific FAQ
  const fetchAnalytics = async (faqId) => {
    try {
      const response = await fetch(`${API_URL}/faqs/${faqId}/analytics`);
      const data = await response.json();
      
      if (data.success) {
        setAnalyticsData(data.data);
        setShowAnalytics(true);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  // Create/Update FAQ
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = showEditModal ? `${API_URL}/faqs/${selectedFAQ.id}` : `${API_URL}/faqs`;
      const method = showEditModal ? "PUT" : "POST";
      
      const payload = {
        ...formData,
        [showEditModal ? "updated_by" : "created_by"]: adminId
      };
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchFAQs();
        resetForm();
        setShowAddModal(false);
        setShowEditModal(false);
      } else {
        setError(data.message || "Failed to save FAQ");
      }
    } catch (error) {
      setError("Failed to save FAQ. Please try again later.");
      console.error("Error saving FAQ:", error);
    }
  };

  // Delete FAQ
  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/faqs/${selectedFAQ.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updated_by: adminId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchFAQs();
        setShowDeleteModal(false);
        setSelectedFAQ(null);
      } else {
        setError(data.message || "Failed to delete FAQ");
      }
    } catch (error) {
      setError("Failed to delete FAQ. Please try again later.");
      console.error("Error deleting FAQ:", error);
    }
  };

  // Toggle FAQ status
  const toggleStatus = async (faq) => {
    try {
      const response = await fetch(`${API_URL}/faqs/${faq.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...faq,
          is_active: !faq.is_active,
          updated_by: adminId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchFAQs();
      } else {
        setError(data.message || "Failed to update FAQ status");
      }
    } catch (error) {
      setError("Failed to update FAQ status. Please try again later.");
      console.error("Error updating FAQ status:", error);
    }
  };

  // Record FAQ analytics
  const recordAnalytics = async (faqId, action) => {
    try {
      await fetch(`${API_URL}/faqs/${faqId}/analytics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_type: "admin",
          action
        })
      });
    } catch (error) {
      console.error("Error recording analytics:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      question: "",
      answer: "",
      category: "general",
      target_audience: "all",
      priority_order: 0,
      is_active: true
    });
  };

  // Open edit modal
  const openEditModal = (faq) => {
    setSelectedFAQ(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      target_audience: faq.target_audience,
      priority_order: faq.priority_order,
      is_active: faq.is_active
    });
    setShowEditModal(true);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchFAQs(), fetchCategories(), fetchStatistics()]);
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Filter FAQs
  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || faq.category === filterCategory;
    const matchesAudience = filterAudience === "all" || faq.target_audience === filterAudience;
    const matchesStatus = filterStatus === "all" || 
                         (filterStatus === "active" && faq.is_active) ||
                         (filterStatus === "inactive" && !faq.is_active);
    
    return matchesSearch && matchesCategory && matchesAudience && matchesStatus;
  });

  useEffect(() => {
    fetchFAQs();
    fetchCategories();
    fetchStatistics();
  }, [filterCategory, filterAudience, filterStatus]);

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case "passenger": return <Users className="text-blue-500" size={16} />;
      case "driver": return <Users className="text-green-500" size={16} />;
      default: return <Users className="text-gray-500" size={16} />;
    }
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.name === category);
    return cat?.color || "#6c757d";
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#002966] flex items-center">
                <HelpCircle className="mr-3" size={32} />
                FAQ Management
              </h1>
              <p className="text-gray-600 mt-2">
                Manage frequently asked questions for mobile app and website
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <BarChart3 size={18} className="mr-2" />
                Analytics
              </button>
              <button
                onClick={handleRefresh}
                className={`p-2 rounded-full hover:bg-gray-100 transition-all ${
                  refreshing ? "animate-spin" : ""
                }`}
              >
                <RefreshCw size={20} className="text-[#002966]" />
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-[#002966] text-white rounded-lg hover:bg-[#001f4d] transition-colors flex items-center"
              >
                <Plus size={18} className="mr-2" />
                Add FAQ
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total FAQs</p>
                    <p className="text-2xl font-bold text-[#002966]">{statistics.total_faqs}</p>
                  </div>
                  <HelpCircle className="text-[#002966]" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active FAQs</p>
                    <p className="text-2xl font-bold text-green-600">{statistics.active_faqs}</p>
                  </div>
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Categories</p>
                    <p className="text-2xl font-bold text-purple-600">{statistics.unique_categories}</p>
                  </div>
                  <Tag className="text-purple-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Passenger FAQs</p>
                    <p className="text-2xl font-bold text-blue-600">{statistics.passenger_faqs}</p>
                  </div>
                  <Users className="text-blue-600" size={24} />
                </div>
              </div>
            </div>
          )}

          {/* Analytics Panel */}
          {showAnalytics && analyticsData && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-[#002966] mb-4">FAQ Analytics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {analyticsData.map((analytic, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium capitalize">{analytic.user_type}</span>
                      <div className="flex items-center space-x-2">
                        <Eye size={16} className="text-blue-500" />
                        <span className="text-sm">{analytic.view_count}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <ThumbsUp size={14} className="text-green-500" />
                        <span>{analytic.helpful_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ThumbsDown size={14} className="text-red-500" />
                        <span>{analytic.not_helpful_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              <select
                value={filterAudience}
                onChange={(e) => setFilterAudience(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
              >
                <option value="all">All Audiences</option>
                <option value="all">All Users</option>
                <option value="passenger">Passengers</option>
                <option value="driver">Drivers</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002966] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading FAQs...</p>
            </div>
          ) : (
            <div>
              {/* FAQs List */}
              {filteredFAQs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                  <HelpCircle size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No FAQs found
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm || filterCategory !== "all" || filterAudience !== "all" || filterStatus !== "all"
                      ? "No FAQs match your current filters."
                      : "Get started by adding your first FAQ."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredFAQs.map((faq) => (
                    <div key={faq.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-[#002966]">{faq.question}</h3>
                              <div className="flex items-center space-x-2">
                                {getAudienceIcon(faq.target_audience)}
                                <span 
                                  className="px-2 py-1 rounded-full text-xs font-medium"
                                  style={{ backgroundColor: getCategoryColor(faq.category) + "20", color: getCategoryColor(faq.category) }}
                                >
                                  {faq.category}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  faq.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                                }`}>
                                  {faq.is_active ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </div>
                            <div className="text-gray-600 mb-3">
                              {expandedFAQ === faq.id ? faq.answer : `${faq.answer.substring(0, 150)}${faq.answer.length > 150 ? "..." : ""}`}
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Calendar size={16} className="mr-1" />
                                {new Date(faq.created_at).toLocaleDateString()}
                              </div>
                              <div className="flex items-center">
                                <MessageSquare size={16} className="mr-1" />
                                Priority: {faq.priority_order}
                              </div>
                              {faq.created_by_name && (
                                <div className="flex items-center">
                                  <Users size={16} className="mr-1" />
                                  {faq.created_by_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => {
                                setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id);
                                recordAnalytics(faq.id, 'view');
                              }}
                              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                              title={expandedFAQ === faq.id ? "Collapse" : "Expand"}
                            >
                              {expandedFAQ === faq.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </button>
                            <button
                              onClick={() => fetchAnalytics(faq.id)}
                              className="p-2 text-gray-500 hover:text-purple-600 transition-colors"
                              title="View Analytics"
                            >
                              <BarChart3 size={20} />
                            </button>
                            <button
                              onClick={() => toggleStatus(faq)}
                              className={`p-2 rounded-lg transition-colors ${
                                faq.is_active 
                                  ? "text-gray-500 hover:text-yellow-600" 
                                  : "text-gray-500 hover:text-green-600"
                              }`}
                              title={faq.is_active ? "Deactivate" : "Activate"}
                            >
                              {faq.is_active ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            <button
                              onClick={() => openEditModal(faq)}
                              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <Edit size={20} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedFAQ(faq);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add/Edit Modal */}
          {(showAddModal || showEditModal) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-semibold text-[#002966]">
                    {showEditModal ? "Edit FAQ" : "Add New FAQ"}
                  </h2>
                </div>
                <form onSubmit={handleSubmit} className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.question}
                        onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
                        placeholder="Enter the FAQ question..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Answer *
                      </label>
                      <textarea
                        required
                        value={formData.answer}
                        onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966] h-32"
                        placeholder="Enter the FAQ answer..."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
                        >
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Target Audience
                        </label>
                        <select
                          value={formData.target_audience}
                          onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
                        >
                          <option value="all">All Users</option>
                          <option value="passenger">Passengers</option>
                          <option value="driver">Drivers</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority Order
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.priority_order}
                          onChange={(e) => setFormData({ ...formData, priority_order: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002966]"
                        />
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 text-[#002966] border-gray-300 rounded focus:ring-[#002966]"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                        Active (visible to users)
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setShowEditModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#002966] text-white rounded-lg hover:bg-[#001f4d] transition-colors flex items-center"
                    >
                      <Save size={18} className="mr-2" />
                      {showEditModal ? "Update" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && selectedFAQ && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <AlertCircle className="text-red-500 mr-3" size={24} />
                    <h2 className="text-xl font-semibold text-gray-900">Delete FAQ</h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to delete the FAQ: <strong>"{selectedFAQ.question}"</strong>?
                    This action can be undone by reactivating the FAQ.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setSelectedFAQ(null);
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FAQManagement;
